import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import DateFnsUtils from "@date-io/date-fns";
import ptBrLocale from "date-fns/locale/pt-BR";
import enLocale from "date-fns/locale/en-US";
import { DebounceInput } from "react-debounce-input";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid,
  InputAdornment,
  Table as MuiTable,
  TableBody,
  TableCell as MuiTableCell,
  TableRow as MuiTableRow,
  TextField,
  Typography
} from "@material-ui/core";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider
} from "@material-ui/pickers";

import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  HighlightOff as HighlightOffIcon,
  AccessTime as AccessTimeIcon,
  Loop as LoopIcon,
  Search as SearchIcon
} from "@material-ui/icons";
import { theme as appTheme } from "../../../assets/theme/StyledTheme";

const Table = styled(MuiTable)`
  width: calc(100% + 60px);
  max-height: calc(100vh - 430px);
  margin: 0 -30px -20px;
  border: solid #f2f2f2;
  border-width: 2px 0 2px 0;
`;
const TableRow = styled(MuiTableRow)`
  cursor: pointer;
`;
const TableCell = styled(MuiTableCell)`
  padding: 20px 7px;
  border-bottom: solid #f2f2f2 2px;
  max-width: 280px;
  min-width: 110px;
  color: #727272;

  &.title {
    padding-left: 30px;
    h5 {
      margin: 0;
      font-size: 22px;
      font-weight: 300;
      color: #000;
      line-height: 1.15em;
      margin-bottom: 4px;
    }
    span {
      font-size: 16px;
    }
  }
  &.class {
    * {
      color: #727272;
    }
    font-size: 18px;
  }
  &.status {
    svg {
      color: ${props => props.color};
      font-size: 28px;
    }
    p {
      font-size: 16px;
      font-weight: 500;
      padding-right: 30px;
      color: ${props => props.color};
    }
  }
`;

const SupportList = ({ supports, filters, setFilter, onSelectSupport }) => {
  const { t, i18n } = useTranslation();
  const dateLocale =
    i18n.language && i18n.language.toLowerCase().startsWith("en")
      ? enLocale
      : ptBrLocale;

  const getStatusLabel = statusId => {
    switch (statusId) {
      case 1:
        return t("support.status.waiting");
      case 2:
      case 3:
        return t("support.status.inProgress");
      case 4:
        return t("support.status.done");
      case 5:
      default:
        return t("support.status.cancelled");
    }
  };

  return (
    <Box>
      <Box pb={4} pt={2}>
        <form noValidate autoComplete="off">
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
            <Grid container spacing={8}>
              <Grid item sm={6}>
                <DebounceInput
                  minLength={3}
                  debounceTimeout={400}
                  element={TextField}
                  value={filters.searchText}
                  onChange={e => setFilter("searchText", e.target.value)}
                  label={t("common.actions.filter")}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item sm={3}>
                <KeyboardDatePicker
                  disableToolbar
                  variant="inline"
                  format="dd/MM/yyyy"
                  margin="normal"
                  id="date-picker-inline"
                  label={t("support.filter.dateFrom")}
                  value={filters.initialDate}
                  onChange={date => {
                    setFilter("initialDate", date);
                  }}
                  KeyboardButtonProps={{
                    "aria-label": t("support.filter.selectFrom")
                  }}
                  fullWidth
                  invalidDateMessage={t("common.messages.invalidDate")}
                />
              </Grid>
              <Grid item sm={3}>
                <KeyboardDatePicker
                  disableToolbar
                  variant="inline"
                  format="dd/MM/yyyy"
                  margin="normal"
                  id="date-picker-inline"
                  label={t("support.filter.dateTo")}
                  value={filters.finalDate}
                  onChange={date => setFilter("finalDate", date)}
                  KeyboardButtonProps={{
                    "aria-label": t("support.filter.selectTo")
                  }}
                  fullWidth
                  invalidDateMessage={t("common.messages.invalidDate")}
                />
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>
        </form>
      </Box>
      <Table aria-label="simple table">
        <TableBody>
          {supports.map(support => {
            const statusText = getStatusLabel(support.statusId);
            let statusIcon;
            let statusColor;
            switch (support.statusId) {
              case 1:
                statusIcon = <AccessTimeIcon />;
                statusColor = appTheme.color.secondary.main;
                break;
              case 2:
              case 3:
                statusIcon = <LoopIcon />;
                statusColor = "#727272";
                break;
              case 4:
                statusIcon = <CheckCircleOutlineIcon />;
                statusColor = appTheme.color.primary.main;
                break;
              case 5:
              default:
                statusIcon = <HighlightOffIcon />;
                statusColor = appTheme.color.error.main;
            }
            return (
              <TableRow
                key={support.id}
                hover
                onClick={() => onSelectSupport(support)}
              >
                <TableCell className="title" align="left">
                  <h5>{support.SupportReason.name}</h5>
                  <span>
                    {moment(support.createdAt).format("DD/MM/YYYY[ às ]HH:mm")}
                  </span>
                </TableCell>
                <TableCell className="class" align="center">
                  <Grid
                    container
                    direction="row"
                    alignItems="center"
                    justify="center"
                    spacing={1}
                  >
                    <Grid item>
                      <Typography variant="body1">
                        {support.SupportReason.SupportType.name}
                      </Typography>
                    </Grid>
                  </Grid>
                </TableCell>
                <TableCell className="status" align="right" color={statusColor}>
                  <Grid
                    container
                    direction="row-reverse"
                    alignItems="center"
                    spacing={1}
                  >
                    <Grid item>
                      <Typography variant="body1">{statusText}</Typography>
                    </Grid>
                    <Grid item>{statusIcon}</Grid>
                  </Grid>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

SupportList.propTypes = {
  filters: PropTypes.shape({
    searchText: PropTypes.string.isRequired,
    initialDate: PropTypes.instanceOf(Date),
    finalDate: PropTypes.instanceOf(Date)
  }).isRequired,
  onSelectSupport: PropTypes.func.isRequired,
  setFilter: PropTypes.func.isRequired,
  supports: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      statusId: PropTypes.number.isRequired,
      createdAt: PropTypes.string.isRequired,
      SupportReason: PropTypes.shape({
        name: PropTypes.string.isRequired,
        SupportType: PropTypes.shape({
          name: PropTypes.string.isRequired
        }).isRequired
      }).isRequired
    })
  ).isRequired
};

export default SupportList;
